import React from 'react';
import { Card, Badge, Button } from '../components/design-system';
import { TrendingUp, Users, FileText, Activity } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const DashboardPage = () => {
  const { t } = useTranslation();
  
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('pages.dashboard.title')}</h1>
          <p className="text-gray-600 mt-1">{t('pages.dashboard.subtitle')}</p>
        </div>
        <Button>{t('pages.dashboard.exportReport')}</Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={<Users className="h-6 w-6 text-blue-600" />}
          title={t('pages.dashboard.stats.totalUsers')}
          value="2,543"
          change="+12%"
          trend="up"
        />
        <StatCard
          icon={<Activity className="h-6 w-6 text-green-600" />}
          title={t('pages.dashboard.stats.activeNow')}
          value="847"
          change="+5%"
          trend="up"
        />
        <StatCard
          icon={<FileText className="h-6 w-6 text-purple-600" />}
          title={t('pages.dashboard.stats.documents')}
          value="1,234"
          change="+8%"
          trend="up"
        />
        <StatCard
          icon={<TrendingUp className="h-6 w-6 text-orange-600" />}
          title={t('pages.dashboard.stats.growth')}
          value="23.5%"
          change="+2.3%"
          trend="up"
        />
      </div>

      {/* Charts and Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title={t('pages.dashboard.activityOverview.title')} subtitle={t('pages.dashboard.activityOverview.subtitle')}>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <p className="text-gray-500">{t('pages.dashboard.activityOverview.placeholder')}</p>
          </div>
        </Card>

        <Card title={t('pages.dashboard.transactions.title')} subtitle={t('pages.dashboard.transactions.subtitle')}>
          <div className="space-y-3">
            <TransactionItem
              title="User registration"
              description="New user signed up"
              time="2 min ago"
              status="success"
            />
            <TransactionItem
              title="Document uploaded"
              description="report-2024.pdf"
              time="15 min ago"
              status="success"
            />
            <TransactionItem
              title="Payment processed"
              description="$49.99 subscription"
              time="1 hour ago"
              status="success"
            />
            <TransactionItem
              title="API request failed"
              description="Retry in progress"
              time="2 hours ago"
              status="warning"
            />
          </div>
        </Card>
      </div>

      {/* Additional Content */}
      <Card title={t('pages.dashboard.systemStatus.title')} subtitle={t('pages.dashboard.systemStatus.subtitle')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatusItem label="API Server" status="operational" />
          <StatusItem label="Database" status="operational" />
          <StatusItem label="Storage" status="operational" />
        </div>
      </Card>
    </div>
  );
};

const StatCard = ({ icon, title, value, change, trend }) => (
  <Card padding="default">
    <div className="flex items-start justify-between">
      <div className="p-2 bg-gray-100 rounded-lg">{icon}</div>
      <Badge variant={trend === 'up' ? 'success' : 'danger'} size="sm">
        {change}
      </Badge>
    </div>
    <div className="mt-4">
      <p className="text-sm text-gray-600">{title}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
    </div>
  </Card>
);

const TransactionItem = ({ title, description, time, status }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
    <div className="flex-1">
      <p className="text-sm font-medium text-gray-900">{title}</p>
      <p className="text-xs text-gray-500">{description}</p>
    </div>
    <div className="flex items-center gap-3">
      <span className="text-xs text-gray-500">{time}</span>
      <Badge variant={status} size="sm">
        {status}
      </Badge>
    </div>
  </div>
);

const StatusItem = ({ label, status }) => (
  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
    <span className="text-sm font-medium text-gray-900">{label}</span>
    <div className="flex items-center gap-2">
      <div className="h-2 w-2 bg-green-500 rounded-full"></div>
      <span className="text-xs text-gray-600 capitalize">{status}</span>
    </div>
  </div>
);
